import { resolveDefaultRoute } from "@/lib/permissions";
import type { User } from "@/types";

export function isSafeInternalPath(path: string | null | undefined): path is string {
  return Boolean(path && path.startsWith("/") && !path.startsWith("//"));
}

export function readSafeAuthNextPath(search: string | URLSearchParams): string | null {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const next = params.get("next");
  return isSafeInternalPath(next) ? next : null;
}

export function acceptInviteLoginPath(organizationId?: string | null): string {
  const next = organizationId
    ? `/accept-invite?organizationId=${encodeURIComponent(organizationId)}`
    : "/accept-invite";
  return `/login?next=${encodeURIComponent(next)}`;
}

function isAcceptInvitePath(path: string): boolean {
  return path === "/accept-invite" || path.startsWith("/accept-invite?");
}

function readOrganizationIdFromPath(path: string): string | null {
  const queryIndex = path.indexOf("?");
  if (queryIndex === -1) return null;
  return new URLSearchParams(path.slice(queryIndex)).get("organizationId")?.trim() || null;
}

export function needsOrgAcceptInvite(user: User): boolean {
  return user.memberStatus === "invited";
}

export function needsCollaboratorOnboarding(user: User): boolean {
  return Boolean(user.needsWorkerOnboarding && user.organization);
}

function isAcceptInviteForOrganization(user: User, organizationId: string): boolean {
  const membership = user.organizations.find((org) => org.id === organizationId);
  return membership?.memberStatus === "invited";
}

export function resolvePostAuthRoute(user: User | null | undefined): string {
  if (!user) return "/login";
  if (user.needsPlatformInviteAccept) return "/accept-invite";
  if (!user.organization) {
    const invitedOrg = user.organizations.find((org) => org.memberStatus === "invited");
    if (invitedOrg) return `/accept-invite?organizationId=${encodeURIComponent(invitedOrg.id)}`;
    return user.canOnboard ? "/onboarding" : "/no-org-access";
  }
  if (needsOrgAcceptInvite(user)) {
    return `/accept-invite?organizationId=${encodeURIComponent(user.organization.id)}`;
  }
  if (needsCollaboratorOnboarding(user)) {
    return "/onboarding";
  }
  return resolveDefaultRoute(user);
}

/**
 * After login, honor an invite `next` so an existing user already in another org
 * still lands on accept-invite for the new membership.
 */
export function resolveLoggedInDestination(
  user: User | null | undefined,
  nextPath?: string | null,
): string {
  if (!user) return "/login";

  if (isSafeInternalPath(nextPath) && isAcceptInvitePath(nextPath)) {
    const organizationId = readOrganizationIdFromPath(nextPath);
    if (user.needsPlatformInviteAccept && !organizationId) {
      return "/accept-invite";
    }
    if (organizationId && isAcceptInviteForOrganization(user, organizationId)) {
      return `/accept-invite?organizationId=${encodeURIComponent(organizationId)}`;
    }
  }

  return resolvePostAuthRoute(user);
}
